import React, { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../utils/config";
import AvatarViewer from "../components/AvatarViewer";
import PasswordInput from "../components/PasswordInput";
import { fetchGoogleConfig, renderGoogleButton } from "../utils/googleSignIn";

function Profile() {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwStatus, setPwStatus] = useState("");
  const [pwError, setPwError] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const fileInputRef = useRef(null);

  // Akun Google (optional link, so Google login works for this account too)
  const [googleCfg, setGoogleCfg] = useState(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleMsg, setGoogleMsg] = useState(null); // { type: "success" | "error", text }
  const googleBtnRef = useRef(null);

  const token = localStorage.getItem("accessToken");

  const fetchMe = () => {
    if (!token) {
      setLoadingUser(false);
      setUser(null);
      return;
    }
    fetch(`${API_BASE_URL}/api/auth/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setUser(data);
        setLoadingUser(false);
      })
      .catch(() => {
        setUser(null);
        setLoadingUser(false);
      });
  };

  useEffect(() => {
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    fetchGoogleConfig().then((cfg) => {
      if (!cancelled) setGoogleCfg(cfg);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Both link and unlink answer with the refreshed /me payload.
  const callGoogleLink = useCallback(
    async (method, body) => {
      setGoogleMsg(null);
      setGoogleBusy(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/google/link/`, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: body ? JSON.stringify(body) : undefined,
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.detail || "Permintaan ke akun Google gagal.");
        setUser(data);
        return true;
      } catch (err) {
        setGoogleMsg({ type: "error", text: err.message });
        return false;
      } finally {
        setGoogleBusy(false);
      }
    },
    [token]
  );

  const handleLinkGoogle = useCallback(
    async (credential) => {
      if (await callGoogleLink("POST", { credential })) {
        setGoogleMsg({ type: "success", text: "Akun Google berhasil dihubungkan." });
      }
    },
    [callGoogleLink]
  );

  const handleUnlinkGoogle = async () => {
    if (!window.confirm("Lepas akun Google dari akun ini? Kamu tetap bisa masuk pakai username & password.")) return;
    if (await callGoogleLink("DELETE")) {
      setGoogleMsg({ type: "success", text: "Akun Google dilepas." });
    }
  };

  // Google's button only exists while there is nothing linked yet.
  const userLoaded = !!user;
  const googleLinked = !!user?.google_linked;
  useEffect(() => {
    if (!googleCfg?.enabled || !userLoaded || googleLinked || !googleBtnRef.current) return undefined;
    let cancelled = false;
    renderGoogleButton({
      clientId: googleCfg.client_id,
      element: googleBtnRef.current,
      onCredential: handleLinkGoogle,
      text: "continue_with",
    }).catch(() => {
      if (!cancelled) {
        setGoogleMsg({ type: "error", text: "Tombol Google gagal dimuat. Cek koneksi internet, lalu muat ulang halaman." });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [googleCfg, userLoaded, googleLinked, handleLinkGoogle]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError("");
    setAvatarUploading(true);
    const form = new FormData();
    form.append("avatar", file);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/avatar/`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload gagal.");
      setUser(data);
    } catch (err) {
      setAvatarError(err.message);
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setPwError("");
    setPwStatus("");

    if (!pwOld || !pwNew || !pwConfirm) {
      setPwError("All password fields are required.");
      return;
    }
    if (pwNew !== pwConfirm) {
      setPwError("New password and confirmation do not match.");
      return;
    }
    if (!token) {
      setPwError("You are not logged in.");
      return;
    }

    fetch(`${API_BASE_URL}/api/auth/change-password/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ old_password: pwOld, new_password: pwNew }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.detail || "Failed to change password.");
        return data;
      })
      .then((data) => {
        setPwStatus(data.detail || "Password changed successfully.");
        setPwOld("");
        setPwNew("");
        setPwConfirm("");
      })
      .catch((err) => {
        setPwError(err.message || "Failed to change password.");
      });
  };

  const initials = user?.username
    ? user.username.substring(0, 2).toUpperCase()
    : "??";

  if (!token) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-4">My Profile</h1>
        <p className="text-sm text-red-600">You are not logged in. Please login again.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">My Profile</h1>

      {loadingUser ? (
        <p className="text-sm text-muted">Loading profile...</p>
      ) : user ? (
        <>
          {/* Profile info card */}
          <div className="glass-card p-4 mb-5">
            <h2 className="text-sm font-semibold mb-4">Account Information</h2>

            {/* Avatar */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative shrink-0">
                {user.avatar_url ? (
                  <AvatarViewer
                    src={user.avatar_url}
                    alt="avatar"
                    className="w-20 h-20 rounded-full ring-2 ring-white/30"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold bg-[var(--accent)] text-[var(--accent-fg)]">
                    {initials}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white dark:bg-slate-700 shadow border border-white/50 dark:border-white/20 flex items-center justify-center hover:scale-110 transition-transform disabled:opacity-50"
                  title="Ganti foto"
                >
                  {avatarUploading ? (
                    <svg className="w-3.5 h-3.5 animate-spin text-violet-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>

              <div>
                <p className="text-base font-semibold text-strong">{user.username}</p>
                <p className="text-xs text-muted capitalize">{user.role}</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="mt-1.5 text-xs text-violet-500 hover:underline disabled:opacity-50"
                >
                  {avatarUploading ? "Mengupload..." : user.avatar_url ? "Ganti foto" : "Upload foto"}
                </button>
                {avatarError && (
                  <p className="text-xs text-red-500 mt-1">{avatarError}</p>
                )}
              </div>
            </div>

            <div className="text-sm space-y-1 border-t border-white/20 dark:border-white/10 pt-3">
              <p>
                <span className="font-medium text-muted">Username:</span>{" "}
                {user.username}
              </p>
              <p>
                <span className="font-medium text-muted">Role:</span>{" "}
                {user.role}
              </p>
            </div>
          </div>

          {/* Akun Google card: hidden when the feature is off, unless an account
              is already linked (it must still be possible to unlink it). */}
          {(googleCfg?.enabled || googleLinked) && (
            <div className="glass-card p-4 mb-5 max-w-lg">
              <h2 className="text-sm font-semibold mb-1">Akun Google</h2>
              {googleLinked ? (
                <>
                  <p className="text-xs text-muted mb-3">
                    Kamu bisa masuk lewat tombol Google di halaman login. Username & password tetap berlaku.
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="badge badge-success">Terhubung</span>
                    <span className="text-sm break-all">{user.google_email}</span>
                    <button
                      type="button"
                      onClick={handleUnlinkGoogle}
                      disabled={googleBusy}
                      className="btn-outline btn-sm ml-auto"
                    >
                      {googleBusy ? "Memproses…" : "Lepas"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted mb-3">
                    Hubungkan akun Google biar bisa masuk tanpa ngetik password. Opsional kok,
                    login pakai username & password tetap jalan.
                  </p>
                  {/* colorScheme "light": see the note on the login page. */}
                  <div ref={googleBtnRef} className="min-h-[44px]" style={{ colorScheme: "light" }} />
                </>
              )}
              {googleMsg && (
                <div
                  className={`mt-3 text-xs rounded-md px-3 py-2 border ${
                    googleMsg.type === "success"
                      ? "note-success"
                      : "note-danger"
                  }`}
                >
                  {googleMsg.text}
                </div>
              )}
            </div>
          )}

          {/* Change password card */}
          <div className="glass-card p-4 max-w-lg">
            <h2 className="text-sm font-semibold mb-3">Change Password</h2>

            <form onSubmit={handleChangePassword} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Old password</label>
                <PasswordInput
                  value={pwOld}
                  onChange={(e) => setPwOld(e.target.value)}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">New password</label>
                <PasswordInput
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">Confirm new password</label>
                <PasswordInput
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
              </div>

              {pwError && (
                <div className="note-danger">
                  {pwError}
                </div>
              )}
              {pwStatus && (
                <div className="note-success">
                  {pwStatus}
                </div>
              )}

              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-md bg-brand-600 text-white text-sm font-semibold px-4 py-2 hover:bg-brand-700"
              >
                Save New Password
              </button>
            </form>
          </div>
        </>
      ) : (
        <p className="text-sm text-red-600">Failed to load profile. Please login again.</p>
      )}
    </div>
  );
}

export default Profile;
